/**
 * SafetyFlagPill — small color-coded badge for a deterministic safety flag.
 *
 * Severity → color mapping:
 *   info     → blue   (informational, not actionable)
 *   warning  → amber  (worth attention)
 *   caution  → red    (call for action — recovery / deload)
 *
 * Title fallback: the flag id, rendered as a human-readable label by
 * collapsing underscores to spaces. The full human-readable description
 * lives in api/utils/weekly_prompt._FLAG_DESCRIPTIONS — the UI repeats
 * it via i18n so localization stays possible.
 */
import { useTranslation } from 'react-i18next';
import type { WeeklyFlag } from '../types/api.ts';

const severityClasses: Record<WeeklyFlag['severity'], string> = {
  info: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  warning: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  caution: 'bg-red-500/15 text-red-300 border-red-500/40',
};

function humanLabel(id: string): string {
  return id.replaceAll('_', ' ');
}

export function SafetyFlagPill({ flag }: Readonly<{ flag: WeeklyFlag }>) {
  const { t } = useTranslation();
  // Descriptions live in a shared top-level "flagDesc" key so both the
  // weekly and monthly detail pages get tooltip text for free. Falling
  // back to the weekDetail-scoped key keeps older weekly translations
  // working until they're migrated.
  const description =
    t(`flagDesc.${flag.id}`, { defaultValue: '' }) ||
    t(`weekDetail.flagDesc.${flag.id}`, { defaultValue: '' });
  const label = humanLabel(flag.id);

  return (
    <span
      title={description || label}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${severityClasses[flag.severity]}`}
    >
      <span>{label}</span>
      <span className="opacity-70">·</span>
      <span className="font-mono opacity-90">
        {Number.isInteger(flag.value) ? flag.value : flag.value.toFixed(2)}
      </span>
    </span>
  );
}
